import type { FormProps } from '@mts/components/Form';
import type { ActionBtnItem, ActionHandlerFn } from '@mts/render';
import type { Fn } from '@mts-fe/types';
import type { Options } from 'sortablejs';
import type { PropType } from 'vue';
import { propTypes } from '@mts-fe/utils';
import type {
  BasicColumn,
  BasicTableProps,
  FetchSetting,
  PaginationProps,
  SizeType,
  SorterResult,
  TableCustomRecord,
  TableRowSelection,
  TableSetting,
} from './types/table';
import { DEFAULT_FILTER_FN, DEFAULT_SIZE, DEFAULT_SORT_FN, FETCH_SETTING } from './const';

export const basicProps = {
  /** @deprecated */
  tableMode: { type: String, default: 'TABLE' },
  isMounted: { type: Boolean, default: false },
  clickToRowSelect: { type: Boolean, default: false },
  isTreeTable: Boolean,
  tableSetting: Object as PropType<TableSetting>,
  inset: Boolean,
  sortFn: {
    type: Function as PropType<(sortInfo: SorterResult) => any>,
    default: DEFAULT_SORT_FN,
  },
  filterFn: {
    type: Function as PropType<(data: Partial<Recordable<string[]>>) => any>,
    default: DEFAULT_FILTER_FN,
  },
  showTableSetting: Boolean,
  editable: { type: Boolean, default: true },
  sortable: { type: Boolean, default: false },
  sortableOptions: {
    type: Object as PropType<Options & { onTableDataChanged?: (dataSource: Recordable[]) => void }>,
    default: () => {},
  },
  autoCreateKey: { type: Boolean, default: true },
  striped: { type: Boolean, default: false },
  indentSize: propTypes.number.def(24),
  api: {
    type: Function as PropType<(...arg: any[]) => Promise<any>>,
    default: null,
  },
  beforeFetch: {
    type: Function as PropType<Fn>,
    default: null,
  },
  afterFetch: {
    type: Function as PropType<Fn>,
    default: null,
  },
  handleSearchInfoFn: {
    type: Function as PropType<Fn>,
    default: null,
  },
  fetchSetting: {
    type: Object as PropType<Partial<FetchSetting>>,
    default: () => {
      return FETCH_SETTING;
    },
  },
  // 立即请求接口
  immediate: { type: Boolean, default: true },
  emptyDataIsShowTable: { type: Boolean, default: true },
  // 缓存searchInfo的key
  searchCacheKey: String,
  // 额外的请求参数
  searchInfo: {
    type: Object as PropType<Recordable>,
    default: null,
  },
  // 默认的排序参数
  defSort: {
    type: Object as PropType<Recordable>,
    default: null,
  },
  // 使用搜索表单
  useSearchForm: propTypes.bool,
  // 表单配置
  formConfig: {
    type: Object as PropType<Partial<FormProps>>,
    default: null,
  },
  columns: {
    type: Array as PropType<BasicColumn[]>,
    default: () => [],
  },
  showIndexColumn: { type: Boolean, default: false },
  indexColumnProps: {
    type: Object as PropType<BasicColumn>,
    default: null,
  },
  actionColumn: {
    type: Object as PropType<BasicColumn>,
    default: null,
  },
  actionHandler: {
    type: Function as PropType<ActionHandlerFn>,
    default: null,
  },
  actions: {
    type: Array as PropType<Partial<ActionBtnItem>[]>,
    default: () => [],
  },
  ellipsis: { type: Boolean, default: true },
  isCanResizeParent: { type: Boolean, default: false },
  canResize: { type: Boolean, default: true },
  clearSelectOnPageChange: propTypes.bool,
  resizeHeightOffset: propTypes.number.def(0),
  rowSelection: {
    type: Object as PropType<TableRowSelection | null>,
    default: null,
  },
  showSelectionBar: propTypes.bool,
  title: {
    type: [String, Function] as PropType<string | ((data: Recordable) => string)>,
    default: null,
  },
  getSubTitle: {
    type: Function as PropType<Fn<any, string>>,
  },
  titleHelpMessage: {
    type: [String, Array] as PropType<string | string[]>,
  },
  usedHeight: propTypes.number,
  maxHeight: propTypes.number,
  minHeight: propTypes.number,
  dataSource: {
    type: Array as PropType<Recordable[]>,
    default: null,
  },
  rowKey: {
    type: [String, Function] as PropType<BasicTableProps['rowKey']>,
    default: '',
  },
  bordered: { type: Boolean, default: true },
  pagination: {
    type: [Object, Boolean] as PropType<PaginationProps | false>,
    default: null,
  },
  loading: propTypes.bool,
  rowClassName: {
    type: Function as PropType<(record: TableCustomRecord<any>, index: number) => string>,
  },
  scroll: {
    type: Object as PropType<BasicTableProps['scroll']>,
  },
  size: {
    type: String as PropType<SizeType>,
    default: DEFAULT_SIZE,
  },
};
